import { useParams, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { VisitsCardListV2 } from 'components/visitors-list/v2/mobile';
import { useIsSmallViewportWidth } from '@hqo/react-components-library/dist/device';
import { getPaginatedVisitsState, getAllFilters, getVisitsLoading } from 'store/visits/selectors';
import { VisitUiType } from 'store/visits/types';
import { OwnerParams } from 'shared/types';
import { VisitsListV2Header } from 'components/visitors-list/v2/header';
import { VisitorsListV2 } from 'components/visitors-list/v2/desktop';
import { FiltersToolbar } from 'components/v2/filters';
import { DatePickerWithPresets } from 'components/v2/date-picker-with-presets';
import { useTimeZone } from 'shared/hooks/use-timezone';
import { useVisitsDateFilter } from 'hooks/use-visits-date-filter.hook';
import { useFetchCompanyOptions } from 'store/companies/hooks';
import { useVisitsEmptyState } from 'hooks/use-visits-empty-state.hook';
import { useBuildingSettings } from 'hooks/use-building-settings.hook';
import { EmptyState } from 'components/v2/empty-state';

export const VisitsViewPageV2 = (): JSX.Element => {
  const { ownerId } = useParams<OwnerParams>();
  const isSmallViewport = useIsSmallViewportWidth();
  const { data: visits } = useSelector(getPaginatedVisitsState(VisitUiType.ALL));
  const filters = useSelector(getAllFilters);
  const isLoading = useSelector(getVisitsLoading);
  const timezone = useTimeZone();
  useBuildingSettings();

  const {
    startDate,
    endDate,
    selectedTimeFrame,
    handleStartDateChange,
    handleEndDateChange,
    handleTimeFrameChange,
    after,
    before,
  } = useVisitsDateFilter({ ownerId });

  useFetchCompanyOptions({ ownerId, after, before });

  const { isEmpty, areFiltersActive } = useVisitsEmptyState({
    visits,
    filters,
    selectedTimeFrame,
    endDate,
  });

  return (
    <div className="h-full bg-white overflow-x-hidden" data-cy="visits-view-page-v2">
      <VisitsListV2Header />
      <div className="max-w-[1600px] mx-auto flex flex-col gap-4 pt-4 pb-12 px-4 sm:px-12 overflow-x-hidden min-h-[600px]">
        <DatePickerWithPresets
          value={startDate || undefined}
          valueEnd={endDate || undefined}
          onValueChange={handleStartDateChange}
          onValueEndChange={handleEndDateChange}
          selectedTimeFrame={selectedTimeFrame || undefined}
          onTimeFrameChange={handleTimeFrameChange}
          timezone={timezone}
        />
        <FiltersToolbar />
        {isEmpty && !isLoading ? (
          <EmptyState filtered={areFiltersActive} selectedTimeFrame={selectedTimeFrame} />
        ) : (
          <>
            {!isSmallViewport && (
              <VisitorsListV2
                visits={visits}
                isLoading={isLoading}
                buildingId={ownerId}
                after={after}
                before={before}
              />
            )}
            {isSmallViewport && (
              <VisitsCardListV2
                visits={visits}
                isLoading={isLoading}
                buildingId={ownerId}
                after={after}
                before={before}
              />
            )}
          </>
        )}
      </div>
      <Outlet />
    </div>
  );
};
